import { strToU8, zipSync } from "fflate";
import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import {
  buildCvBlocks,
  cvFontFamilies,
  fontFamilyForText,
  isRtlText,
  normalizeCv,
  normalizeCvTemplate
} from "./ats-cv-core.js";
import { preparePdfText } from "./ats-cv-text-layout.js";

const PAGE_SIZES = Object.freeze({ A4: [595.28, 841.89], LETTER: [612, 792] });
const DOCX_FONT_NAMES = Object.freeze({
  latin: "Arial",
  georgian: "Noto Sans Georgian",
  arabic: "Noto Sans Arabic",
  hebrew: "Noto Sans Hebrew",
  japanese: "Noto Sans JP",
  korean: "Noto Sans KR",
  chinese: "Noto Sans SC"
});
const BASE_PDF_THEME = Object.freeze({
  margin: 46,
  text: rgb(0.08, 0.1, 0.14),
  muted: rgb(0.28, 0.32, 0.38),
  accent: rgb(0.08, 0.1, 0.14),
  target: rgb(0.28, 0.32, 0.38),
  rule: rgb(0.72, 0.75, 0.8),
  nameSize: 22,
  nameLineHeight: 25,
  targetSize: 11,
  targetLineHeight: 14,
  contactSize: 9,
  contactLineHeight: 12,
  sectionSize: 10.5,
  sectionBefore: 8,
  sectionRuleThickness: 0.7,
  sectionAfter: 7,
  entrySize: 10.5,
  entryLineHeight: 13,
  metaSize: 9.2,
  metaLineHeight: 12,
  bodySize: 9.7,
  bodyLineHeight: 13,
  bodyAfter: 4,
  bulletAfter: 1
});
const PDF_THEMES = Object.freeze({
  classic: BASE_PDF_THEME,
  modern: Object.freeze({
    ...BASE_PDF_THEME,
    accent: rgb(0.32, 0.26, 0.78),
    target: rgb(0.32, 0.26, 0.78),
    rule: rgb(0.56, 0.51, 0.93),
    nameSize: 24,
    sectionRuleThickness: 1.4
  }),
  minimal: Object.freeze({
    ...BASE_PDF_THEME,
    margin: 52,
    accent: rgb(0.22, 0.25, 0.29),
    nameSize: 20,
    sectionBefore: 11,
    sectionRuleThickness: 0,
    sectionAfter: 5
  }),
  compact: Object.freeze({
    ...BASE_PDF_THEME,
    margin: 40,
    nameSize: 20,
    nameLineHeight: 22,
    targetSize: 10,
    targetLineHeight: 12,
    contactSize: 8.3,
    contactLineHeight: 10.5,
    sectionSize: 9.6,
    sectionBefore: 5,
    sectionAfter: 4,
    entrySize: 9.7,
    entryLineHeight: 11.5,
    metaSize: 8.5,
    metaLineHeight: 10.5,
    bodySize: 8.9,
    bodyLineHeight: 11,
    bodyAfter: 2,
    bulletAfter: 0
  }),
  executive: Object.freeze({
    ...BASE_PDF_THEME,
    accent: rgb(0.09, 0.23, 0.38),
    target: rgb(0.6, 0.36, 0.13),
    targetUppercase: true,
    rule: rgb(0.09, 0.23, 0.38),
    nameSize: 25,
    targetSize: 10.5,
    sectionSize: 11,
    sectionRuleThickness: 1.6
  })
});

const BASE_DOCX_THEME = Object.freeze({
  font: "Arial",
  margin: 920,
  defaultSize: 21,
  textColor: "17191E",
  nameSize: 44,
  nameColor: "17191E",
  nameAfter: 20,
  targetSize: 23,
  targetColor: "46505F",
  contactSize: 19,
  sectionSize: 21,
  sectionColor: "17191E",
  sectionBefore: 140,
  sectionAfter: 50,
  borderSize: 4,
  borderColor: "B8BEC7",
  entrySize: 21,
  entryColor: "17191E",
  entryBefore: 45,
  metaSize: 19,
  paragraphAfter: 80,
  lineSpacing: 252,
  bulletAfter: 20,
  bulletIndent: 240
});
const DOCX_THEMES = Object.freeze({
  classic: BASE_DOCX_THEME,
  modern: Object.freeze({
    ...BASE_DOCX_THEME,
    nameSize: 48,
    nameColor: "5143C8",
    targetColor: "5143C8",
    sectionColor: "5143C8",
    borderSize: 10,
    borderColor: "8F83EE"
  }),
  minimal: Object.freeze({
    ...BASE_DOCX_THEME,
    margin: 1040,
    nameSize: 40,
    nameColor: "363C47",
    sectionSize: 19,
    sectionColor: "363C47",
    sectionBefore: 180,
    borderSize: 0
  }),
  compact: Object.freeze({
    ...BASE_DOCX_THEME,
    margin: 800,
    defaultSize: 18,
    nameSize: 40,
    targetSize: 20,
    contactSize: 17,
    sectionSize: 19,
    sectionBefore: 90,
    sectionAfter: 30,
    entrySize: 19,
    entryBefore: 25,
    metaSize: 17,
    paragraphAfter: 45,
    lineSpacing: 220,
    bulletAfter: 10,
    bulletIndent: 210
  }),
  executive: Object.freeze({
    ...BASE_DOCX_THEME,
    nameSize: 50,
    nameColor: "173B61",
    targetColor: "9A5B20",
    targetCaps: true,
    sectionSize: 22,
    sectionColor: "173B61",
    borderSize: 10,
    borderColor: "173B61",
    entryColor: "173B61"
  })
});

export function createCvDocx(source, settings = {}) {
  const cv = normalizeCv(source);
  const blocks = buildCvBlocks(cv);
  const pageSize = settings.pageSize === "LETTER" ? "LETTER" : "A4";
  const template = normalizeCvTemplate(settings.template);
  const documentXml = wordDocumentXml(blocks, pageSize, template, cvFontFamilies(cv));
  const files = {
    "[Content_Types].xml": utf8(contentTypesXml()),
    "_rels/.rels": utf8(packageRelationshipsXml()),
    "docProps/core.xml": utf8(corePropertiesXml(cv.personal.name)),
    "docProps/app.xml": utf8(applicationPropertiesXml()),
    "word/document.xml": utf8(documentXml),
    "word/styles.xml": utf8(stylesXml(template)),
    "word/_rels/document.xml.rels": utf8(documentRelationshipsXml())
  };
  return zipSync(files, { level: 6 });
}

export async function createCvPdf(source, settings = {}, fontBytes) {
  const fontEntries = fontBytes?.regular && fontBytes?.bold
    ? [["latin", fontBytes]]
    : Object.entries(fontBytes || {}).filter(([, value]) => value?.regular && value?.bold);
  if (!fontEntries.length) throw new Error("The CV fonts could not be loaded.");
  const cv = normalizeCv(source);
  const document = await PDFDocument.create();
  document.registerFontkit(fontkit);
  document.setTitle(`${cv.personal.name || "ATS"} CV`);
  document.setAuthor(cv.personal.name || "");
  document.setCreator("smallpdf.app");
  document.setProducer("smallpdf.app ATS CV Builder");
  const fonts = Object.fromEntries(await Promise.all(fontEntries.map(async ([family, bytes]) => {
    const regular = await document.embedFont(bytes.regular, { subset: false });
    const bold = await document.embedFont(bytes.bold, { subset: false });
    if (family === "arabic" || family === "hebrew") {
      forceVisualGlyphOrder(regular);
      forceVisualGlyphOrder(bold);
    }
    return [family, { regular, bold }];
  })));
  const layout = new CvPdfLayout(
    document,
    fonts,
    settings.pageSize === "LETTER" ? "LETTER" : "A4",
    normalizeCvTemplate(settings.template)
  );
  for (const block of buildCvBlocks(cv)) layout.draw(block);
  if (!layout.pages.length) throw new Error("Add CV content before exporting.");
  return document.save({ useObjectStreams: true });
}

function forceVisualGlyphOrder(font) {
  const sourceFont = font?.embedder?.font;
  if (!sourceFont?.glyphForCodePoint) return;
  sourceFont.layout = text => {
    const glyphs = Array.from(text, character => sourceFont.glyphForCodePoint(character.codePointAt(0)));
    return {
      glyphs,
      positions: glyphs.map(glyph => ({
        xAdvance: glyph.advanceWidth,
        yAdvance: 0,
        xOffset: 0,
        yOffset: 0
      }))
    };
  };
}

class CvPdfLayout {
  constructor(document, fonts, pageSize, template) {
    this.document = document;
    this.fonts = fonts;
    this.fontFamilies = Object.keys(fonts);
    this.pageSize = pageSize;
    this.theme = PDF_THEMES[template];
    this.margin = this.theme.margin;
    this.pages = [];
    this.page = null;
    this.y = 0;
    this.addPage();
  }

  get pageWidth() {
    return PAGE_SIZES[this.pageSize][0];
  }

  get pageHeight() {
    return PAGE_SIZES[this.pageSize][1];
  }

  get contentWidth() {
    return this.pageWidth - this.margin * 2;
  }

  addPage() {
    this.page = this.document.addPage(PAGE_SIZES[this.pageSize]);
    this.pages.push(this.page);
    this.y = this.pageHeight - this.margin;
  }

  ensure(height) {
    if (this.y - height < this.margin) this.addPage();
  }

  font(text, weight = "regular") {
    const requested = fontFamilyForText(text, this.fontFamilies);
    const family = this.fonts[requested] || this.fonts[this.fontFamilies[0]];
    return family[weight];
  }

  draw(block) {
    const theme = this.theme;
    if (block.type === "name") {
      this.drawWrapped(block.text, {
        size: theme.nameSize,
        font: this.font(block.text, "bold"),
        lineHeight: theme.nameLineHeight,
        color: theme.accent,
        after: 2
      });
      return;
    }
    if (block.type === "target-title") {
      const text = theme.targetUppercase ? block.text.toLocaleUpperCase() : block.text;
      this.drawWrapped(text, {
        size: theme.targetSize,
        font: this.font(text, "bold"),
        lineHeight: theme.targetLineHeight,
        color: theme.target,
        after: 2
      });
      return;
    }
    if (block.type === "contact") {
      this.drawWrapped(block.text, {
        size: theme.contactSize,
        font: this.font(block.text),
        lineHeight: theme.contactLineHeight,
        color: theme.muted,
        after: 7
      });
      return;
    }
    if (block.type === "section") {
      const text = block.text.toLocaleUpperCase();
      this.ensure(31);
      this.y -= theme.sectionBefore;
      const font = this.font(text, "bold");
      const x = isRtlText(text)
        ? this.pageWidth - this.margin - font.widthOfTextAtSize(text, theme.sectionSize)
        : this.margin;
      this.page.drawText(text, {
        x,
        y: this.y - theme.sectionSize,
        size: theme.sectionSize,
        font,
        color: theme.accent
      });
      this.y -= theme.sectionSize + 4.5;
      if (theme.sectionRuleThickness > 0) {
        this.page.drawLine({
          start: { x: this.margin, y: this.y },
          end: { x: this.pageWidth - this.margin, y: this.y },
          thickness: theme.sectionRuleThickness,
          color: theme.rule
        });
      }
      this.y -= theme.sectionAfter;
      return;
    }
    if (block.type === "entry-title") {
      this.ensure(22);
      this.drawWrapped(block.text, {
        size: theme.entrySize,
        font: this.font(block.text, "bold"),
        lineHeight: theme.entryLineHeight,
        color: theme.accent,
        after: 0
      });
      return;
    }
    if (block.type === "entry-meta") {
      const text = [block.left, block.right].filter(Boolean).join(" | ");
      this.drawWrapped(text, {
        size: theme.metaSize,
        font: this.font(text),
        lineHeight: theme.metaLineHeight,
        color: theme.muted,
        after: 2
      });
      return;
    }
    if (block.type === "bullet") {
      this.drawWrapped(block.text, {
        size: theme.bodySize,
        font: this.font(block.text),
        lineHeight: theme.bodyLineHeight,
        indent: 12,
        prefix: "•",
        after: theme.bulletAfter
      });
      return;
    }
    this.drawWrapped(block.text, {
      size: theme.bodySize,
      font: this.font(block.text),
      lineHeight: theme.bodyLineHeight,
      after: theme.bodyAfter
    });
  }

  drawWrapped(text, options) {
    const indent = options.indent || 0;
    const available = this.contentWidth - indent;
    const lines = wrapText(String(text || "").replace(/\s+/g, " ").trim(), options.font, options.size, available);
    const rtl = isRtlText(text);
    if (!lines.length) return;
    for (let index = 0; index < lines.length; index += 1) {
      this.ensure(options.lineHeight);
      const baseline = this.y - options.size;
      if (options.prefix && index === 0) {
        const prefixWidth = options.font.widthOfTextAtSize(options.prefix, options.size);
        this.page.drawText(options.prefix, {
          x: rtl ? this.pageWidth - this.margin - prefixWidth : this.margin,
          y: baseline,
          size: options.size,
          font: options.font,
          color: options.color || this.theme.text
        });
      }
      const displayLine = rtl ? preparePdfText(lines[index]) : lines[index];
      const lineWidth = options.font.widthOfTextAtSize(displayLine, options.size);
      this.page.drawText(displayLine, {
        x: rtl ? this.pageWidth - this.margin - indent - lineWidth : this.margin + indent,
        y: baseline,
        size: options.size,
        font: options.font,
        color: options.color || this.theme.text
      });
      this.y -= options.lineHeight;
    }
    this.y -= options.after || 0;
  }
}

function wrapText(text, font, size, maximumWidth) {
  if (!text) return [];
  const width = value => font.widthOfTextAtSize(isRtlText(value) ? preparePdfText(value) : value, size);
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (width(candidate) <= maximumWidth) {
      line = candidate;
      continue;
    }
    if (line) lines.push(line);
    line = "";
    if (width(word) <= maximumWidth) {
      line = word;
      continue;
    }
    for (const character of [...word]) {
      const expanded = `${line}${character}`;
      if (line && width(expanded) > maximumWidth) {
        lines.push(line);
        line = character;
      } else {
        line = expanded;
      }
    }
  }
  if (line) lines.push(line);
  return lines;
}

function wordDocumentXml(blocks, pageSize, template, fontFamilies) {
  const theme = DOCX_THEMES[template];
  const paragraphs = blocks.map(block => wordParagraph(block, theme, fontFamilies)).join("");
  const [width, height] = pageSize === "LETTER" ? [12240, 15840] : [11906, 16838];
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>${paragraphs}
    <w:sectPr>
      <w:pgSz w:w="${width}" w:h="${height}"/>
      <w:pgMar w:top="${theme.margin}" w:right="${theme.margin}" w:bottom="${theme.margin}" w:left="${theme.margin}" w:header="0" w:footer="0" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`;
}

function wordParagraph(block, theme, fontFamilies) {
  const styles = {
    name: "CvName",
    "target-title": "TargetTitle",
    contact: "Contact",
    section: "SectionHeading",
    "entry-title": "EntryTitle",
    paragraph: "Normal",
    bullet: "Bullet"
  };
  const styleId = styles[block.type] || "Normal";
  const rawText = block.type === "entry-meta"
    ? [block.left, block.right].filter(Boolean).join(" | ")
    : block.text;
  const text = block.type === "section" ? rawText.toLocaleUpperCase() : rawText;
  const rtl = isRtlText(text);
  const fontName = DOCX_FONT_NAMES[fontFamilyForText(text, fontFamilies)] || theme.font;
  const prefix = styleId === "Bullet" ? "• " : "";
  return `<w:p><w:pPr><w:pStyle w:val="${styleId}"/>${directParagraphProperties(styleId, theme, rtl)}</w:pPr><w:r><w:rPr>${directRunProperties(styleId, theme, fontName, rtl)}</w:rPr><w:t xml:space="preserve">${xml(prefix + String(text || ""))}</w:t></w:r></w:p>`;
}

function directParagraphProperties(styleId, theme, rtl) {
  const bidi = rtl ? "<w:bidi/>" : "";
  const properties = {
    Normal: `${bidi}<w:spacing w:after="${theme.paragraphAfter}" w:line="${theme.lineSpacing}" w:lineRule="auto"/>`,
    CvName: `<w:keepNext/>${bidi}<w:spacing w:after="${theme.nameAfter}"/>`,
    TargetTitle: `<w:keepNext/>${bidi}<w:spacing w:after="20"/>`,
    Contact: `${bidi}<w:spacing w:after="100"/>`,
    SectionHeading: `<w:keepNext/>${sectionBorderXml(theme)}${bidi}<w:spacing w:before="${theme.sectionBefore}" w:after="${theme.sectionAfter}"/>`,
    EntryTitle: `<w:keepNext/>${bidi}<w:spacing w:before="${theme.entryBefore}" w:after="0"/>`,
    EntryMeta: `<w:keepNext/>${bidi}<w:spacing w:after="35"/>`,
    Bullet: `${bidi}<w:spacing w:after="${theme.bulletAfter}" w:line="${theme.lineSpacing}" w:lineRule="auto"/><w:ind w:left="${theme.bulletIndent}" w:hanging="${theme.bulletIndent}"/>`
  };
  return properties[styleId] || properties.Normal;
}

function directRunProperties(styleId, theme, fontName, rtl) {
  const values = {
    Normal: { size: theme.defaultSize, color: theme.textColor },
    CvName: { size: theme.nameSize, color: theme.nameColor, bold: true },
    TargetTitle: { size: theme.targetSize, color: theme.targetColor, bold: true, caps: theme.targetCaps },
    Contact: { size: theme.contactSize, color: "46505F" },
    SectionHeading: { size: theme.sectionSize, color: theme.sectionColor, bold: true, caps: true },
    EntryTitle: { size: theme.entrySize, color: theme.entryColor, bold: true },
    EntryMeta: { size: theme.metaSize, color: "46505F" },
    Bullet: { size: theme.defaultSize, color: theme.textColor }
  };
  const value = values[styleId] || values.Normal;
  return `<w:rFonts w:ascii="${fontName}" w:hAnsi="${fontName}" w:eastAsia="${fontName}" w:cs="${fontName}"/>${value.bold ? "<w:b/>" : ""}${value.caps ? "<w:caps/>" : ""}${rtl ? "<w:rtl/>" : ""}<w:color w:val="${value.color}"/><w:sz w:val="${value.size}"/><w:szCs w:val="${value.size}"/>`;
}

function sectionBorderXml(theme) {
  return theme.borderSize > 0
    ? `<w:pBdr><w:bottom w:val="single" w:sz="${theme.borderSize}" w:space="3" w:color="${theme.borderColor}"/></w:pBdr>`
    : "";
}

function stylesXml(template) {
  const theme = DOCX_THEMES[template];
  const sectionBorder = sectionBorderXml(theme);
  const targetCaps = theme.targetCaps ? "<w:caps/>" : "";
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults>
    <w:rPrDefault><w:rPr><w:rFonts w:ascii="${theme.font}" w:hAnsi="${theme.font}" w:eastAsia="${theme.font}" w:cs="${theme.font}"/><w:sz w:val="${theme.defaultSize}"/><w:szCs w:val="${theme.defaultSize}"/></w:rPr></w:rPrDefault>
    <w:pPrDefault><w:pPr><w:spacing w:after="${theme.paragraphAfter}" w:line="${theme.lineSpacing}" w:lineRule="auto"/></w:pPr></w:pPrDefault>
  </w:docDefaults>
  ${style("Normal", "Normal", `<w:qFormat/>`)}
  ${style("CvName", "CV Name", `<w:basedOn w:val="Normal"/><w:next w:val="TargetTitle"/><w:pPr><w:keepNext/><w:spacing w:after="${theme.nameAfter}"/></w:pPr><w:rPr><w:b/><w:color w:val="${theme.nameColor}"/><w:sz w:val="${theme.nameSize}"/><w:szCs w:val="${theme.nameSize}"/></w:rPr>`)}
  ${style("TargetTitle", "Target Title", `<w:basedOn w:val="Normal"/><w:next w:val="Contact"/><w:pPr><w:keepNext/><w:spacing w:after="20"/></w:pPr><w:rPr><w:b/>${targetCaps}<w:color w:val="${theme.targetColor}"/><w:sz w:val="${theme.targetSize}"/><w:szCs w:val="${theme.targetSize}"/></w:rPr>`)}
  ${style("Contact", "Contact", `<w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="100"/></w:pPr><w:rPr><w:color w:val="46505F"/><w:sz w:val="${theme.contactSize}"/><w:szCs w:val="${theme.contactSize}"/></w:rPr>`)}
  ${style("SectionHeading", "Section Heading", `<w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:pPr><w:keepNext/>${sectionBorder}<w:spacing w:before="${theme.sectionBefore}" w:after="${theme.sectionAfter}"/></w:pPr><w:rPr><w:b/><w:caps/><w:color w:val="${theme.sectionColor}"/><w:sz w:val="${theme.sectionSize}"/><w:szCs w:val="${theme.sectionSize}"/></w:rPr>`)}
  ${style("EntryTitle", "Entry Title", `<w:basedOn w:val="Normal"/><w:next w:val="EntryMeta"/><w:pPr><w:keepNext/><w:spacing w:before="${theme.entryBefore}" w:after="0"/></w:pPr><w:rPr><w:b/><w:color w:val="${theme.entryColor}"/><w:sz w:val="${theme.entrySize}"/><w:szCs w:val="${theme.entrySize}"/></w:rPr>`)}
  ${style("EntryMeta", "Entry Meta", `<w:basedOn w:val="Normal"/><w:next w:val="Bullet"/><w:pPr><w:keepNext/><w:spacing w:after="35"/></w:pPr><w:rPr><w:color w:val="46505F"/><w:sz w:val="${theme.metaSize}"/><w:szCs w:val="${theme.metaSize}"/></w:rPr>`)}
  ${style("Bullet", "Bullet", `<w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="${theme.bulletAfter}"/><w:ind w:left="${theme.bulletIndent}" w:hanging="${theme.bulletIndent}"/></w:pPr>`)}
</w:styles>`;
}

function style(id, name, contents) {
  return `<w:style w:type="paragraph" w:styleId="${id}"><w:name w:val="${name}"/>${contents}</w:style>`;
}

function contentTypesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`;
}

function packageRelationshipsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`;
}

function documentRelationshipsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
}

function corePropertiesXml(author) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>ATS CV</dc:title><dc:creator>${xml(author || "smallpdf.app")}</dc:creator><cp:lastModifiedBy>${xml(author || "smallpdf.app")}</cp:lastModifiedBy>
</cp:coreProperties>`;
}

function applicationPropertiesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes"><Application>smallpdf.app</Application></Properties>`;
}

function xml(value) {
  return String(value || "").replace(/[&<>"']/g, character => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&apos;"
  })[character]);
}

function utf8(value) {
  return strToU8(value);
}
